<script setup lang="ts">
import MarketingFlowTooltipConversion from '@/views/autopilot/Components/MarketingFlowTooltipConversion.vue'
import AppStatsCard from '@/components/ui/AppStatsCard.vue'
import MarketingFlowTooltipRevenue from '@/views/autopilot/Components/MarketingFlowTooltipRevenue.vue'
import MarketingFlowTooltipExecution from '@/views/autopilot/Components/MarketingFlowTooltipExecution.vue'

import type { Statistics } from '@/views/autopilot/MarketingFlowPage.vue'

const props = defineProps<{
  statistics: Statistics | null
}>()

const { t } = useI18n()
const { formatNumber, formatPercentage } = useNumber()

const formatStatistics = computed(() => {
  return [
    {
      title: t('pages.marketingFlow.marketingFlowPerformance.statistics.executions'),
      value: props.statistics?.count_messages ? formatNumber(props.statistics?.count_messages, false) : '-',
      tooltip: 'executions'
    },
    {
      title: t('pages.marketingFlow.marketingFlowPerformance.statistics.revenueGross'),
      value: props.statistics?.revenue_formatted || '-',
      tooltip: 'revenue'
    },
    {
      title: t('pages.marketingFlow.marketingFlowPerformance.statistics.visits'),
      value: props.statistics?.count_visit_conversions
        ? formatNumber(props.statistics?.count_visit_conversions, false)
        : '-',
      subValue: props.statistics?.visit_conversion_percentage
        ? formatPercentage(props.statistics?.visit_conversion_percentage, 0)
        : undefined,
      tooltip: 'conversion'
    }
  ]
})
</script>

<template>
  <div>
    <h2 class="mb-4 text-lg font-semibold text-foreground">
      {{ t('pages.marketingFlow.marketingFlowPerformance.globalPerformance') }}
    </h2>

    <div class="grid grid-cols-1 gap-5 md:grid-cols-12">
      <AppStatsCard v-for="statistic in formatStatistics" :key="statistic.title" class="col-span-1 md:col-span-4">
        <template #title>
          <div class="flex items-center gap-2">
            <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
              {{ statistic.title }}
            </span>
            <MarketingFlowTooltipExecution v-if="statistic.tooltip === 'executions'" />
            <MarketingFlowTooltipConversion v-if="statistic.tooltip === 'conversion'" />
            <MarketingFlowTooltipRevenue v-if="statistic.tooltip === 'revenue'" />
          </div>
        </template>
        <template #content>
          <div class="flex items-baseline justify-start gap-2">
            <span class="text-3xl font-extrabold text-primary">
              {{ statistic.value }}
            </span>
            <span v-if="statistic.subValue" class="text-muted-foreground">
              {{ statistic.subValue }}
            </span>
          </div>
        </template>
      </AppStatsCard>
    </div>
  </div>
</template>
